// OAuth consent screen (Developer Platform H5). A third-party application sends the signed-in user
// here (/oauth/consent?client_id=…&redirect_uri=…&scope=…&code_challenge=…&state=…). The user reviews
// the requested access and either allows — we mint a PKCE-bound authorization code and navigate the
// browser to the app's redirect URI with ?code=…&state=… — or denies, returning ?error=access_denied.
// The backend re-validates the client, exact redirect URI, scope subset and PKCE, so this screen is
// presentation + the explicit user grant, not the security boundary.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AppWindow, Check, Loader2, ShieldAlert, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { authorizeApplication, getConsentAppInfo, getScopeCatalog, type ConsentAppInfo, type ScopeCatalogEntry } from '@/services/developerService';
import { getApiErrorMessage } from '@/lib/apiError';
import { scopesToDisplay } from './consentScopes';

export const OAuthConsentPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [params] = useSearchParams();

  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const scopeParam = params.get('scope') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256';
  const state = params.get('state') || '';
  const responseType = params.get('response_type') || 'code';
  // RFC 8707 resource indicator — ChatGPT/MCP hosts send it on the authorize
  // URL; forward it so the server can reject unknown targets at consent time.
  const resource = params.get('resource') || '';

  // RFC 6749 lets the client omit scope (the server defaults to the app's
  // registered scopes — which the backend /authorize already does). Mirror
  // that here so the consent screen can DESCRIBE what will be granted.
  const [appInfo, setAppInfo] = useState<ConsentAppInfo | null>(null);
  // Show ONLY what will be granted (request ∩ the app's registration) — see consentScopes.ts.
  const requestedScopes = useMemo(() => scopesToDisplay(scopeParam, appInfo?.scopes), [scopeParam, appInfo]);

  const [descriptions, setDescriptions] = useState<Record<string, ScopeCatalogEntry>>({});
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);

  // Name the requester + surface provenance (best-effort; the backend still
  // re-validates everything at /authorize).
  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    getConsentAppInfo(clientId)
      .then((info) => { if (alive) setAppInfo(info); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [clientId]);

  // Best-effort scope descriptions (only admins can read the catalog; fall back to bare names).
  useEffect(() => {
    let alive = true;
    getScopeCatalog()
      .then((cats) => {
        if (!alive) return;
        const map: Record<string, ScopeCatalogEntry> = {};
        cats.forEach((c) => c.scopes.forEach((s) => { map[s.scope] = s; }));
        setDescriptions(map);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  // Validate the request shape before offering to authorize.
  const invalid = useMemo(() => {
    if (responseType !== 'code') return t('oauth.err_response_type', { defaultValue: 'Unsupported response_type (only "code" is supported).' });
    if (!clientId) return t('oauth.err_client', { defaultValue: 'Missing client_id.' });
    if (!redirectUri) return t('oauth.err_redirect', { defaultValue: 'Missing redirect_uri.' });
    try { new URL(redirectUri); } catch { return t('oauth.err_redirect_invalid', { defaultValue: 'redirect_uri is not a valid URL.' }); }
    if (!codeChallenge || codeChallenge.length < 43) return t('oauth.err_pkce', { defaultValue: 'A PKCE code_challenge is required.' });
    if (requestedScopes.length === 0 && !(scopeParam.trim() === '' && appInfo === null)) {
      return t('oauth.err_scope', { defaultValue: 'No scopes were requested.' });
    }
    return null;
  }, [responseType, clientId, redirectUri, codeChallenge, requestedScopes, scopeParam, appInfo, t]);

  const denyRedirect = () => {
    try {
      const url = new URL(redirectUri);
      url.searchParams.set('error', 'access_denied');
      if (state) url.searchParams.set('state', state);
      window.location.href = url.toString();
    } catch {
      toast.error(t('oauth.err_redirect_invalid', { defaultValue: 'redirect_uri is not a valid URL.' }));
    }
  };

  const allow = async () => {
    setSubmitting('allow');
    try {
      const { redirectTo } = await authorizeApplication({
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: scopeParam.trim() ? requestedScopes.join(' ') : undefined,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
        ...(state ? { state } : {}),
        ...(resource ? { resource } : {}),
      });
      window.location.href = redirectTo; // hand the browser back to the app with ?code=…&state=…
    } catch (e: any) {
      setSubmitting(null);
      toast.error(getApiErrorMessage(e, t('oauth.authorize_failed', { defaultValue: 'Authorization failed.' })));
    }
  };

  const deny = () => {
    setSubmitting('deny');
    denyRedirect();
  };

  const appHost = (() => { try { return new URL(redirectUri).host; } catch { return redirectUri; } })();

  return (
    <div className="max-w-md mx-auto mt-10">
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
              <AppWindow className="w-6 h-6 text-foreground" />
            </div>
            <h1 className="text-h3 text-charcoal">
              {t('oauth.title', { defaultValue: 'Authorize application' })}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('oauth.subtitle', {
                defaultValue: 'An application ({{client}}) is requesting access to your KaziPeople data. It will send results to {{host}}.',
                client: appInfo?.name || clientId,
                host: appHost,
              })}
            </p>
            {appInfo?.source === 'dcr' && !appInfo.certified && (
              <div className="mt-3 rounded-lg px-3 py-2 text-xs border bg-warning-soft border-warning text-warning-strong flex items-start gap-2 text-left">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {t('oauth.unverified_warning', {
                    defaultValue: 'This app registered itself and has not been verified by KaziPeople. Only continue if you started this connection yourself and trust it.',
                  })}
                </span>
              </div>
            )}
          </div>

          {invalid ? (
            <div className="rounded-lg px-3 py-3 text-sm border bg-danger-soft border-danger text-danger-strong flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{invalid}</span>
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('oauth.will_access', { defaultValue: 'This will allow the application to:' })}
                </p>
                <ul className="space-y-1.5">
                  {requestedScopes.map((s) => {
                    const meta = descriptions[s];
                    return (
                      <li key={s} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2">
                        <Check className="w-4 h-4 mt-0.5 text-success shrink-0" />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-foreground font-mono">{s}</span>
                            {meta?.sensitive && (
                              <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-warning-soft text-warning-strong border border-warning">
                                {t('developer.sensitive', { defaultValue: 'sensitive' })}
                              </span>
                            )}
                          </span>
                          {meta?.description && <span className="block text-xs text-muted-foreground">{meta.description}</span>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <p className="text-[11px] text-muted-foreground">
                {t('oauth.footer', {
                  defaultValue: 'Only allow access if you trust this application. You are granting access as your signed-in account.',
                })}
              </p>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={deny} disabled={submitting !== null}>
                  {submitting === 'deny' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                  {t('oauth.deny', { defaultValue: 'Deny' })}
                </Button>
                <Button className="flex-1" onClick={allow} disabled={submitting !== null}>
                  {submitting === 'allow' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  {t('oauth.allow', { defaultValue: 'Allow' })}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthConsentPage;
